import React from 'react';
import { useSelector } from 'react-redux';
import { getImpersonationDisplayInfo } from '../../utils/impersonation.utils';
import { RootState } from '../../store/store';

const ImpersonationIndicator: React.FC = () => {
  const { isImpersonating, sessionAge } = getImpersonationDisplayInfo();
  const user = useSelector((state: RootState) => state.user.user);

  if (!isImpersonating) {
    return null;
  }

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    'unknown user';

  const handleEndImpersonation = () => {
    // Close the tab immediately. browserSessionPersistence and sessionStorage
    // are tab-scoped, so they're cleared automatically on tab close — no need
    // to sign out or call clearImpersonationSession() first. Doing those
    // would dispatch setUser(null), trigger UserRouteGuard's cleanup, and
    // delete the shared admin cookie before the tab actually closes.
    window.close();
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-black px-4 py-2 shadow-md">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">
              <strong>Admin Impersonation Mode</strong> - Viewing as user: {displayName}
            </p>
            <p className="text-xs opacity-75">
              Session started {sessionAge} • This tab is impersonating a user
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleEndImpersonation}
            className="text-xs bg-black text-white px-3 py-1 rounded-md hover:bg-gray-800 transition-colors duration-200 font-medium"
          >
            End Impersonation
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationIndicator;
